import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { components } from '@/api/qraft/schema';
import { AlertBox } from '@/common/AlertBox';
import { FormSelect } from '@/common/forms/FormSelect';
import { StyledTextarea } from '~/styles/forms/formElements';
import { HelperInfoBlock } from './HelperInfoBlock';
import {
  InfoBlocksRow,
  IntroHeading,
  IntroText,
} from './StudyHelperRequestModal.styles';

type StudyHelperBoardItem = components['schemas']['StudyHelperBoardItem'];
type SwitchReason = components['schemas']['SwitchReason'];

export interface SwitchModalBodyProps {
  requestedHelper: StudyHelperBoardItem;
  currentHelper?: StudyHelperBoardItem;
  reason: SwitchReason | '';
  reasonError: boolean;
  onReasonChange: (val: SwitchReason | '') => void;
  reasonOther: string;
  reasonOtherError: boolean;
  onReasonOtherChange: (val: string) => void;
}

export function SwitchModalBody({
  requestedHelper,
  currentHelper,
  reason,
  reasonError,
  onReasonChange,
  reasonOther,
  reasonOtherError,
  onReasonOtherChange,
}: SwitchModalBodyProps) {
  const { t } = useTranslation();

  // Gravity Form 5 ("Study Helper Reassign") field 1: a required select whose
  // five choices legacy stores by their label text. v3 posts the SwitchReason
  // slugs instead — the labels are EN-only in the catalogue (domain
  // `gravity_form-5`) while this control is student-facing in four languages,
  // and a slug survives translation where a label does not.
  const switchReasons: Array<{ value: SwitchReason; label: string }> = [
    { value: 'unresponsive', label: t('wbs.unresponsive') },
    { value: 'discouraging', label: t('wbs.discouraging') },
    {
      value: 'inappropriate',
      label: t('wbs.inappropriate-language-or-topics'),
    },
    { value: 'too-many-messages', label: t('wbs.too-many-messages') },
    { value: 'other', label: t('wbs.other') },
  ];

  return (
    <>
      {/* Legacy's ES/FR/PT_BR switch-confirmation posts drop this sentence
          entirely, so the key carries an English value only and i18n's
          fallbackLng serves it everywhere. That absence is parity. */}
      <IntroHeading>{t('wbs.wbs-will-review-your-request')}</IntroHeading>
      <IntroText>{t('wbs.please-provide-the-information-below')}</IntroText>

      <InfoBlocksRow>
        {currentHelper && (
          <HelperInfoBlock
            label={t('wbs.current-study-helper-information')}
            helper={currentHelper}
          />
        )}
        <HelperInfoBlock
          label={t('lessons.requested-study-helper-information')}
          helper={requestedHelper}
        />
      </InfoBlocksRow>

      {(reasonError || reasonOtherError) && (
        <AlertBox variant="error">
          {t('wbs.there-was-a-problem-with-your-submission')}
        </AlertBox>
      )}

      <ReasonField>
        <ReasonLabel htmlFor="switch-reason" $hasError={reasonError}>
          {t('wbs.reason')} <RequiredStar>*</RequiredStar>
        </ReasonLabel>
        <FormSelect
          name="switch-reason"
          value={reason}
          onChange={(val) => onReasonChange(val as SwitchReason | '')}
          options={switchReasons}
          placeholder=""
          fullWidth
          error={reasonError}
        />
        {reasonError && (
          <FieldError>{t('wbs.this-field-is-required')}</FieldError>
        )}
      </ReasonField>

      {/* GF 5 field 5, "Other Reason": a textarea with isRequired true and
          conditionalLogic {show, all, field 1 is "Other"} — so it exists, and is
          required, on that branch alone. 6 of the 11 real legacy entries took
          it, so it carries the majority of the switch signal. */}
      {reason === 'other' && (
        <OtherReasonField>
          <ReasonLabel
            htmlFor="switch-reason-other"
            $hasError={reasonOtherError}
          >
            {t('wbs.other-reason')} <RequiredStar>*</RequiredStar>
          </ReasonLabel>
          {/* maxLength mirrors CreateAdoptionRequestInput.reason_other's server
              bound so the field cannot silently 422. Legacy's textarea has no
              maxlength; the bound is a deliberate divergence. */}
          <OtherReasonTextarea
            id="switch-reason-other"
            value={reasonOther}
            maxLength={2000}
            onChange={(e) => onReasonOtherChange(e.target.value)}
            $hasError={reasonOtherError}
          />
          {reasonOtherError && (
            <FieldError>{t('wbs.this-field-is-required')}</FieldError>
          )}
        </OtherReasonField>
      )}
    </>
  );
}

const ReasonField = styled.div`
  margin-top: 4px;
  width: 50%;
`;

const OtherReasonField = styled.div`
  margin-top: 24px;
  width: 100%;
`;

const OtherReasonTextarea = styled(StyledTextarea)<{ $hasError: boolean }>`
  border-color: ${({ $hasError }) =>
    $hasError ? 'var(--wbs-red)' : 'var(--grey-300)'};
`;

const ReasonLabel = styled.label<{ $hasError?: boolean }>`
  color: ${({ $hasError }) =>
    $hasError ? 'var(--wbs-red)' : 'var(--grey-900)'};
  display: block;
  font-family: var(--raleway);
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  transition: color 0.2s;
`;

const RequiredStar = styled.span`
  color: var(--wbs-red);
  font-weight: 300;
  font-size: 26px;
  line-height: 0;
  vertical-align: middle;
  margin-left: 4px;
`;

const FieldError = styled.p`
  color: var(--wbs-red);
  font-family: var(--raleway);
  font-size: 12px;
  margin: 12px 0 0 0;
`;
